import Joi from 'joi';
import KPI from '../kpi/kpi';
import { TaskTypeFormElement_Translation } from './taskTypeFormElement_Translation';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import ResourceLink from 'models/resourceLink';
import { newGuid } from 'utils/guid';
import { ResourceListType } from 'models/resourceList';
import Task from './task';
import { IAppContext } from 'App/AppContext';
import { apiRequest } from 'services/Auth/authConfig';
import { apiGetControlOrTheme } from 'services/Api/controlService';
import { ControlTypes } from 'models/control';
import type { AuditCriterionOneTimeState } from 'models/auditCriteria';

export enum TaskTypeFieldType {
  KPI = 0,
  AttachmentPage = 1,
  AttachmentDocument = 2,
  AttachmentListItem = 3,
  AttachmentURL = 4,
  ContextProcess = 5,
  ContextObjective = 6,
  ContextAsset = 7,
  ContextRisk = 8,
  ContextControl = 9,
  ContextRequirement = 10,
}

export enum FormFieldType {
  KPI = 0,
  Attachment = 1,
  Context = 2,
}

export default class TaskTypeFormElement {
  taskTypeId: number;

  fieldId: number;

  fieldKey?: string;

  fieldType: TaskTypeFieldType;

  fieldMode?: number;

  kpiId?: number;

  kpiDefinitionRevisionId?: number;

  kpiDefinitionRevisionRowVersion?: string;

  sortOrder: number;

  listId?: number;

  folderId?: string;

  kpi?: KPI;

  //these are server-owned, one-way like kpi.hasAutomatedEvidence: the client renders them but
  //never writes them back, so they are absent from mapToTaskTypeForm

  //the entity this field was generated from; with criterionKey it is the join key back to the criterion
  entityType?: number;

  entityId?: number;

  //set means the field was GENERATED from an entity's audit criterion: the projector owns it, the
  //UI locks the question (the criterion itself is edited through the audit criteria editor)
  criterionKey?: string;

  //the criterion was superseded or archived: read-only when answered, hidden when not, never required
  retiredOn?: Date;

  //server-owned, execution tasks only: the criterion behind this field is outside the audit's
  //standards and nobody answered it, so the form hides it and never requires it. Never posted back.
  notApplicable?: boolean;

  //display only, set ONLY by the local criteria projection: the standards the criterion belongs to,
  //shown as chips in the criteria editor. A server-projected form carries none - the auditee never
  //sees standards on the audit task. Never posted back.
  normIds?: number[];

  //display only, and set ONLY by the local criteria projection (mappings/auditCriteriaFormProjection):
  //there the criterion's own evidence mode is known, while a server-projected form reads "one-time" off
  //the evidence-task bindings. Never posted back, so it is absent from mapToTaskTypeForm as well.
  criterionOneTime?: boolean;

  //display only, set by the same local projection: the criterion's verification ('AI' or 'Manual'),
  //which decides whether the AI advice command applies to it. Never posted back.
  criterionVerification?: string;

  //display only, set by the same local projection: what a re-evaluation did to the criterion since the
  //set's last review ('New', 'Reworded', 'Archived'), shown as a badge. Never posted back.
  criterionChangeKind?: string;

  //display only, set by the same local projection: the one-time state of a criterion the item reads from a generic set
  //(PLAN-075 Task 30), which colours its one-time badge. Never posted back.
  criterionOneTimeState?: AuditCriterionOneTimeState;

  trans: TaskTypeFormElement_Translation[];

  //these are flattened from the trans object
  transIdx: number;

  name: string;

  description?: string;

  expectedResult?: string;

  instruction?: string;

  //these are internal
  key: string; //not part of API, used to uniquely identify this

  errorMessage?: string;

  constructor() {
    this.taskTypeId = 0;
    this.fieldId = 0;
    this.fieldType = 0;
    this.sortOrder = 0;
    this.taskTypeId = 0;
    this.entityType = undefined;
    this.entityId = undefined;
    this.criterionKey = undefined;
    this.retiredOn = undefined;
    this.notApplicable = undefined;
    this.normIds = undefined;
    this.trans = [];
    this.name = '';
    this.transIdx = -1;
    this.key = newGuid();
  }

  //true when the field lets the user add a document: a document attachment field, or a KPI field that
  //takes attachments (the same condition the form uses to show its attachment picker)
  offersDocumentUpload(): boolean {
    if (this.retiredOn || this.notApplicable) return false;
    if (this.fieldType === TaskTypeFieldType.AttachmentDocument) return true;

    return !!this.kpi?.attachmentMode && !this.kpi.hasAutomatedEvidence;
  }

  getAttachments(source: ResourceLink[]): ResourceLink[] {
    const listType = this.getListTypeOfAttachmentFieldType();

    return source.filter((s) => s.list.listType === listType);
  }

  getListTypeOfAttachmentFieldType(): ResourceListType | undefined {
    switch (this.fieldType) {
      case TaskTypeFieldType.AttachmentDocument:
        return ResourceListType.DocumentLibrary;
      case TaskTypeFieldType.AttachmentListItem:
        return ResourceListType.CustomList;
      case TaskTypeFieldType.AttachmentPage:
        return ResourceListType.SitePageLibrary;
      case TaskTypeFieldType.AttachmentURL:
        return ResourceListType.WebURL;
    }

    return undefined;
  }

  getMaxCount = (): number => {
    if (!this.fieldMode) return 0;

    return this.fieldMode < 0 ? Math.abs(this.fieldMode) : 10;
  };

  getMinCount = (): number => {
    if (!this.fieldMode) return 0;

    return this.fieldMode > 0 ? this.fieldMode : 0;
  };

  getCurrentContextCount = async (task: Task, appContext: IAppContext): Promise<number> => {
    switch (this.fieldType) {
      case TaskTypeFieldType.ContextAsset:
        return task.assetIds.length;
      case TaskTypeFieldType.ContextRequirement:
      case TaskTypeFieldType.ContextControl:
        let count = 0;
        try {
          //the array of controls contain both controls and themes. We cannot know how many controls or themes are in the array
          //We need to get the details from the server
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          for (let idx = 0; idx < task.controlIds.length; idx++) {
            const control = await apiGetControlOrTheme(task.controlIds[idx], accessToken, appContext.globalDataCache);
            if (!control) continue;
            if (control.controlType === ControlTypes.Measure && this.fieldType === TaskTypeFieldType.ContextControl) {
              count++;
            } else if (
              control.controlType === ControlTypes.Theme &&
              this.fieldType === TaskTypeFieldType.ContextRequirement
            ) {
              count++;
            }
          }
        } catch (err) {
          appContext.setError(err);
        }

        return count;
      case TaskTypeFieldType.ContextObjective:
        return task.objectiveIds.length;
      case TaskTypeFieldType.ContextProcess:
        return task.processIds.length;
      case TaskTypeFieldType.ContextRisk:
        return task.riskIds.length;
      default:
        return 0;
    }
  };

  isEqual(taskType: TaskTypeFormElement): boolean {
    if (taskType.name !== this.name) return false;
    if (taskType.description !== this.description) return false;
    if (taskType.instruction !== this.instruction) return false;
    if (taskType.expectedResult !== this.expectedResult) return false;
    if (taskType.fieldType !== this.fieldType) return false;
    if (taskType.fieldMode !== this.fieldMode) return false;
    if (taskType.fieldId !== this.fieldId) return false;
    if (taskType.fieldKey !== this.fieldKey) return false;
    if (taskType.taskTypeId !== this.taskTypeId) return false;
    if (taskType.kpiId !== this.kpiId) return false;
    if (taskType.kpiDefinitionRevisionId !== this.kpiDefinitionRevisionId) return false;
    if (taskType.sortOrder !== this.sortOrder) return false;
    if (taskType.listId !== this.listId) return false;
    if (taskType.folderId !== this.folderId) return false;
    if (taskType.entityType !== this.entityType) return false;
    if (taskType.entityId !== this.entityId) return false;
    if (taskType.criterionKey !== this.criterionKey) return false;
    if (taskType.retiredOn?.getTime() !== this.retiredOn?.getTime()) return false;

    return true;
  }

  clone(): TaskTypeFormElement {
    const output = new TaskTypeFormElement();
    output.key = this.key;
    output.taskTypeId = this.taskTypeId;
    output.fieldId = this.fieldId;
    output.fieldKey = this.fieldKey;
    output.fieldType = this.fieldType;
    output.fieldMode = this.fieldMode;
    output.kpi = this.kpi?.clone();
    output.kpiId = this.kpiId;
    output.kpiDefinitionRevisionId = this.kpiDefinitionRevisionId;
    output.kpiDefinitionRevisionRowVersion = this.kpiDefinitionRevisionRowVersion;
    output.sortOrder = this.sortOrder;
    output.listId = this.listId;
    output.description = this.description;
    output.name = this.name;
    output.instruction = this.instruction;
    output.expectedResult = this.expectedResult;
    output.transIdx = this.transIdx;
    output.trans = [...this.trans];
    output.errorMessage = this.errorMessage;
    output.folderId = this.folderId;
    output.entityType = this.entityType;
    output.entityId = this.entityId;
    output.criterionKey = this.criterionKey;
    output.retiredOn = this.retiredOn ? new Date(this.retiredOn) : undefined;
    output.notApplicable = this.notApplicable;
    output.normIds = this.normIds ? [...this.normIds] : undefined;
    output.criterionOneTime = this.criterionOneTime;
    output.criterionVerification = this.criterionVerification;
    output.criterionChangeKind = this.criterionChangeKind;
    output.criterionOneTimeState = this.criterionOneTimeState;

    return output;
  }

  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    const schema: Joi.ObjectSchema = Joi.object({
      name: Joi.string().max(512).required().label(localizedFields['name']),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate(
      {
        name: this.name,
      },
      { abortEarly: false },
    );
  }
}
